import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  QrCode,
  CreditCard,
  IndianRupee,
  TrendingUp,
  Calendar,
  ArrowUpRight,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";

const DashboardPage = () => {
  const { API, getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [scansData, setScansData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, revenueRes, scansRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, getAuthHeader()),
          axios.get(`${API}/dashboard/charts/revenue?days=14`, getAuthHeader()),
          axios.get(`${API}/dashboard/charts/scans?days=14`, getAuthHeader()),
        ]);
        setStats(statsRes.data);
        setRevenueData(revenueRes.data);
        setScansData(scansRes.data);
      } catch (error) {
        console.error("Dashboard fetch error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [API, getAuthHeader]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const kpiCards = [
    {
      title: "Total Customers",
      value: stats?.total_customers || 0,
      icon: Users,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      title: "QR Scans",
      value: stats?.total_scans || 0,
      icon: QrCode,
      color: "text-violet-600",
      bg: "bg-violet-50",
    },
    {
      title: "Total Bookings",
      value: stats?.total_bookings || 0,
      icon: CreditCard,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
    },
    {
      title: "Revenue",
      value: formatCurrency(stats?.total_revenue || 0),
      icon: IndianRupee,
      color: "text-amber-600",
      bg: "bg-amber-50",
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-500">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-950" data-testid="dashboard-title">
          Dashboard
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Overview of airport services performance
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((kpi, index) => (
          <Card key={index} className="border-zinc-200 shadow-none" data-testid={`kpi-${kpi.title.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    {kpi.title}
                  </p>
                  <p className="text-2xl font-bold text-zinc-900 mt-2 tracking-tight">
                    {kpi.value}
                  </p>
                </div>
                <div className={`w-10 h-10 ${kpi.bg} flex items-center justify-center`}>
                  <kpi.icon className={`w-5 h-5 ${kpi.color}`} aria-hidden="true" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Revenue Chart */}
        <Card className="border-zinc-200 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-700 uppercase tracking-wider">
              Revenue (Last 14 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis
                    dataKey="_id"
                    tickFormatter={(val) => val?.slice(5) || ""}
                    tick={{ fontSize: 11, fill: "#71717a" }}
                  />
                  <YAxis
                    tickFormatter={(val) => `₹${val / 1000}k`}
                    tick={{ fontSize: 11, fill: "#71717a" }}
                  />
                  <Tooltip
                    formatter={(value) => [formatCurrency(value), "Revenue"]}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#0A192F"
                    strokeWidth={2}
                    dot={{ fill: "#0A192F", strokeWidth: 0, r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Scans Chart */}
        <Card className="border-zinc-200 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-700 uppercase tracking-wider">
              QR Scans (Last 14 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={scansData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis
                    dataKey="_id"
                    tickFormatter={(val) => val?.slice(5) || ""}
                    tick={{ fontSize: 11, fill: "#71717a" }}
                  />
                  <YAxis tick={{ fontSize: 11, fill: "#71717a" }} />
                  <Tooltip
                    formatter={(value) => [value, "Scans"]}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Bar dataKey="scans" fill="#002FA7" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Service Usage */}
        <Card className="border-zinc-200 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-700 uppercase tracking-wider">
              Top Services
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.service_usage?.length > 0 ? (
                stats.service_usage.slice(0, 5).map((service, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                    <span className="text-sm text-zinc-700 truncate">{service.service_name}</span>
                    <Badge variant="secondary" className="text-xs">
                      {service.count} bookings
                    </Badge>
                  </div>
                ))
              ) : (
                <p className="text-sm text-zinc-400 text-center py-4">No data available</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bookings by Status */}
        <Card className="border-zinc-200 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-700 uppercase tracking-wider">
              Booking Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.bookings_by_status && Object.keys(stats.bookings_by_status).length > 0 ? (
                Object.entries(stats.bookings_by_status).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                    <span className="text-sm text-zinc-700 capitalize">{status.replace(/_/g, " ")}</span>
                    <Badge
                      className={`text-xs ${
                        status === "confirmed"
                          ? "bg-emerald-100 text-emerald-700"
                          : status === "completed"
                          ? "bg-blue-100 text-blue-700"
                          : status === "cancelled"
                          ? "bg-red-100 text-red-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {count}
                    </Badge>
                  </div>
                ))
              ) : (
                <p className="text-sm text-zinc-400 text-center py-4">No bookings yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Bookings */}
        <Card className="border-zinc-200 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-700 uppercase tracking-wider">
              Recent Bookings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.recent_bookings?.length > 0 ? (
                stats.recent_bookings.map((booking) => (
                  <div key={booking.id} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-zinc-700 truncate">
                        {booking.customer?.name || "Unknown"}
                      </p>
                      <p className="text-xs text-zinc-400">{booking.booking_number || booking.id}</p>
                    </div>
                    <span className="text-sm font-medium text-zinc-900">
                      {formatCurrency(booking.total_amount)}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-zinc-400 text-center py-4">No recent bookings</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-zinc-200 shadow-none bg-emerald-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
              <span className="text-xs font-medium text-emerald-700 uppercase tracking-wider">Today's Scans</span>
            </div>
            <p className="text-xl font-bold text-emerald-900 mt-1">{stats?.today_scans || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-zinc-200 shadow-none bg-blue-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-700 uppercase tracking-wider">Today's Bookings</span>
            </div>
            <p className="text-xl font-bold text-blue-900 mt-1">{stats?.today_bookings || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-zinc-200 shadow-none bg-violet-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-violet-600" />
              <span className="text-xs font-medium text-violet-700 uppercase tracking-wider">Active Customers</span>
            </div>
            <p className="text-xl font-bold text-violet-900 mt-1">{stats?.active_customers || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-zinc-200 shadow-none bg-amber-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <ArrowUpRight className="w-4 h-4 text-amber-600" />
              <span className="text-xs font-medium text-amber-700 uppercase tracking-wider">Avg. Booking Value</span>
            </div>
            <p className="text-xl font-bold text-amber-900 mt-1">
              {stats?.total_bookings > 0
                ? formatCurrency(stats.total_revenue / stats.total_bookings)
                : "₹0"}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
